"use client"

import { useRef } from 'react'
import { Quote, Star } from 'lucide-react'
import { useIntersectionObserver } from '@/hooks/use-intersection-observer'
import { useScrollY } from '@/hooks/use-scroll-progress'
import { useStaggeredAnimation } from '@/hooks/use-animation-delay'

const testimonials = [
  
  {
    name: 'Anagha Kharwade',
    role: 'Mother',
    content: 'I had an online consultation with Dr. Arushi for breastfeeding support, and I’m extremely grateful for her guidance. She is truly brilliant, patient, and very knowledgeable. She gave me ample time, listened carefully to all my concerns, and addressed each of my problems in detail. \n\nWith her support and proper guidance, I have been able to exclusively breastfeed my baby successfully. I highly recommend her to any mother seeking breastfeeding consultation and support.',
    avatar: 'AK',
    rating: 5,
    gradient: 'from-indigo-400 to-indigo-600',
  },
  {
    name: 'Charu Sharma',
    role: 'Mother',
    content: 'We feel so lucky to have found Dr. Arushi for our son. Her calm and reassuring nature is exactly what we need as parents. She explains everything clearly and with patience, which helps us feel confident. She provides an invaluable service through online consultation. Knowing that we can reach her for advice at any time, day or night, is a massive relief for my family. There have been a couple of incidents when I reached to her in emergency and she guided us well to handle situation. I highly recommend her.',
    avatar: 'CS',
    rating: 5,
    gradient: 'from-amber-400 to-orange-500',
  },
  {
    name: 'Vishal Dadurwade',
    role: 'Father',
    content: 'As first-time parents, we were extremely anxious, but Dr. Aarushi patiently gave us her valuable time and clear guidance when we were truly worried. The most important thing for us was that she did not prescribe unnecessary medicines. Instead, she took the time to explain why the issue was happening and how it could heal naturally. Her calm approach and thoughtful consultation gave us great confidence and peace of mind.',
    avatar: 'VD',
    rating: 5,
    gradient: 'from-peach to-rose-400',
  },
  {
    name: 'Gunjan Khatri',
    role: 'Aunt',
    content: 'Dr Arushi was very attentive and sweet to my niece when we went for a check up. My niece is not afraid of visiting a doctor now after meeting her :) definitely recommended.',
    avatar: 'GK',
    rating: 5,
    gradient: 'from-teal to-teal-dark',
  },
]

// Testimonial card component
function TestimonialCard({
  testimonial,
  index
}: {
  testimonial: typeof testimonials[0]
  index: number
}) {
  const { ref: cardRef, isIntersecting: isInView } = useIntersectionObserver({ 
    threshold: 0.1, 
    once: true 
  })
  const starItems = useStaggeredAnimation(testimonial.rating, index * 150 + 400, 100)

  return (
    <div
      ref={cardRef}
      className={`testimonial-card relative bg-white rounded-3xl p-6 lg:p-8 cursor-pointer overflow-hidden group card-hover hover:-translate-y-2 hover:shadow-2xl transition-all duration-300 ${isInView ? 'reveal visible' : 'reveal'}`}
      style={{ 
        transitionDelay: `${index * 150}ms`
      }}
    >

      {/* Animated gradient border */}
      <div
        className={`absolute inset-0 bg-linear-to-br ${testimonial.gradient} rounded-3xl -z-10 pointer-events-none scale-95 opacity-0 group-hover:scale-[1.02] group-hover:opacity-15 transition-all duration-400`}
        style={{ filter: "blur(20px)" }}
      />

      {/* Quote icon */}
      <div
        className={`absolute top-6 right-6 ${isInView ? 'scale-in' : 'opacity-0 scale-0 -rotate-45'}`}
        style={{ 
          animationDelay: `${index * 150 + 300}ms`,
          animationDuration: '0.5s',
          animationFillMode: 'forwards'
        }}
      >
        <Quote className="w-12 h-12 text-teal/10 group-hover:text-teal/20 transition-colors duration-500" />
      </div>

      {/* Rating stars */}
      <div className="flex gap-1 mb-4">
        {Array.from({ length: testimonial.rating }).map((_, i) => (
          <div
            key={i}
            className={`${starItems[i] ? 'scale-in' : 'opacity-0 scale-0 -rotate-180'}`}
            style={{ 
              animationDelay: `${index * 150 + 400 + i * 100}ms`,
              animationDuration: '0.5s',
              animationFillMode: 'forwards'
            }}
          >
            <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
          </div>
        ))}
      </div>

      {/* Content */}
      <p
        className={`text-navy/80 leading-relaxed mb-6 text-base lg:text-lg relative z-10 whitespace-pre-line`}
        style={{ animationDelay: `${index * 150 + 500}ms` }}
      >
        &ldquo;{testimonial.content}&rdquo;
      </p>

      {/* Author */}
      <div
        className={`flex items-center gap-4 ${isInView ? 'reveal visible' : 'reveal'}`}
        style={{ transitionDelay: `${index * 150 + 600}ms` }}
      >
        <div className={`w-12 h-12 rounded-full bg-linear-to-br ${testimonial.gradient} flex items-center justify-center text-white font-semibold shadow-lg hover:scale-110 hover:rotate-3 transition-all duration-300`}>
          {testimonial.avatar}
        </div>
        <div>
          <div className="font-semibold text-navy">{testimonial.name}</div>
          <div className="text-sm text-muted-foreground">{testimonial.role}</div>
        </div>
      </div>

      {/* Shine effect */}
      <div className="absolute inset-0 bg-linear-to-r from-transparent via-white/30 to-transparent -translate-x-full pointer-events-none group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />

    </div>
  )
}

export function TestimonialsSection() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const scrollY = useScrollY()
  const { ref: headerRef, isIntersecting: isHeaderInView } = useIntersectionObserver({ 
    threshold: 0.1, 
    once: true 
  })

  // Calculate scroll-based effects
  const backgroundScale = Math.max(0.8, Math.min(1.1, 0.8 + (scrollY / 1000) * 0.3))
  const backgroundOpacity = Math.max(0.3, Math.min(0.8, 0.3 + (scrollY / 1000) * 0.5))

  return (
    <section
      ref={sectionRef}
      className="py-16 lg:py-32 bg-background relative overflow-hidden min-h-dvh w-full flex items-center"
    >
      {/* Animated background decoration */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-linear-to-br from-teal/10 via-transparent to-peach/10 rounded-full blur-3xl"
        style={{ 
          transform: `translate(-50%, -50%) scale(${backgroundScale})`,
          opacity: backgroundOpacity
        }}
      />

      {/* Floating particles */}
      <div
        className="absolute top-20 left-20 w-4 h-4 rounded-full bg-teal/20 hidden sm:block animate-float"
        style={{ animationDuration: '6s' }}
      />
      <div
        className="absolute bottom-32 right-32 w-6 h-6 rounded-full bg-peach/20 hidden sm:block animate-spin"
        style={{ animationDuration: '8s' }}
      />
      <div
        className="absolute top-1/3 right-1/4 w-3 h-3 rounded-full bg-amber-400/30 animate-pulse-soft"
        style={{ animationDuration: '4s' }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div
          ref={headerRef}
          className="text-center max-w-3xl mx-auto mb-12 lg:mb-16"
        >
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal/10 text-teal text-sm font-medium mb-6 hover:scale-105 transition-transform duration-300 ${isHeaderInView ? 'scale-in' : ''}`}
          >
            Testimonials
          </div>

          <h2
            className={`font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-6 text-balance ${isHeaderInView ? 'reveal visible' : 'reveal'}`}
            style={{ transitionDelay: '100ms' }}
          >
            What Parents{' '}
            <span className="text-gradient">Say About Us</span>
          </h2>

          <p
            className={`text-lg text-muted-foreground leading-relaxed ${isHeaderInView ? 'reveal visible' : 'reveal'}`}
            style={{ transitionDelay: '200ms' }}
          >
            Join thousands of families who trust Dr. Arushi with their children&apos;s health and well-being.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={testimonial.name}
              testimonial={testimonial}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
